/**
 * cryptoUtils.js — AES-256-CBC + HKDF-SHA256 utilities
 *
 * Matches device firmware and Python script EXACTLY:
 *   K1 = HKDF-SHA256(salt=MAC, ikm=BASE_SECRET, info="WAAS-K1-MASTER")
 *   K2 decryption: raw AES-256-CBC (NO PKCS#7 padding from Lambda)
 *   Payload encryption: AES-256-CBC with NULL padding (matches firmware)
 *   Payload decryption: AES-256-CBC, strip trailing nulls
 */

const BASE_SECRET =
  '7F3A91C4D8E25B6A4F10C9ED2A7348B5E61D0F9A3C57B84216A9DE0C4B71F235';
const INFO_STRING = 'WAAS-K1-MASTER';

// ── hex helpers ─────────────────────────────────────────────────
export function hexToBuffer(hex) {
  const clean = hex.replace(/\s+/g, '');
  const bytes = new Uint8Array(clean.length / 2);
  for (let i = 0; i < bytes.length; i++)
    bytes[i] = parseInt(clean.substr(i * 2, 2), 16);
  return bytes;
}

export function bufferToHex(buf) {
  return Array.from(new Uint8Array(buf))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

export function stringToHex(str) {
  return Array.from(new TextEncoder().encode(str))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

export function hexToString(hex) {
  const bytes = hexToBuffer(hex);
  // strip trailing null bytes (matches Python's .rstrip(b'\x00'))
  let end = bytes.length;
  while (end > 0 && bytes[end - 1] === 0) end--;
  return new TextDecoder().decode(bytes.slice(0, end));
}

// ── HMAC-SHA256 ─────────────────────────────────────────────────
async function hmacSha256(keyBytes, msgBytes) {
  const key = await crypto.subtle.importKey(
    'raw', keyBytes, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign'],
  );
  return new Uint8Array(await crypto.subtle.sign('HMAC', key, msgBytes));
}

// ── K1 derivation (HKDF-SHA256) ─────────────────────────────────
//    PRK = HMAC-SHA256(key=MAC_bytes, msg=BASE_SECRET_bytes)
//    K1  = HMAC-SHA256(key=PRK,       msg="WAAS-K1-MASTER")
export async function generateK1(macId) {
  const macClean   = macId.replace(/[^0-9a-fA-F]/g, '').toLowerCase();
  const macBytes   = hexToBuffer(macClean);
  const secretBytes = hexToBuffer(BASE_SECRET);
  const infoBytes  = new TextEncoder().encode(INFO_STRING);

  const prk = await hmacSha256(macBytes, secretBytes);   // Extract
  const k1  = await hmacSha256(prk, infoBytes);          // Expand
  return bufferToHex(k1);
}

// ── AES-256-CBC decrypt (handles BOTH padded and unpadded data) ─
//    Lambda encrypts K2 WITHOUT PKCS#7 (K2 is exactly 32 bytes).
//    Device responses use null-padding.
//    WebCrypto requires PKCS#7, so we forge a valid padding block
//    when the data has no PKCS#7 padding.
export async function aesDecrypt(payloadHex, keyHex) {
  const raw      = hexToBuffer(payloadHex);
  const iv       = raw.slice(0, 16);
  const ct       = raw.slice(16);
  const keyBytes = hexToBuffer(keyHex);

  const key = await crypto.subtle.importKey(
    'raw', keyBytes, { name: 'AES-CBC' }, false, ['encrypt', 'decrypt'],
  );

  // Strategy: try normal decrypt first (works if data has valid PKCS#7);
  // if it fails, forge a padding block (for null-padded or unpadded data)
  try {
    const plain = await crypto.subtle.decrypt({ name: 'AES-CBC', iv }, key, ct);
    return bufferToHex(plain);
  } catch (_) {
    // Normal decrypt failed → data has no PKCS#7 padding
    // Forge a valid padding block and append it
    const lastBlock = ct.slice(ct.length - 16);
    const paddingPlain = new Uint8Array(16).fill(16);  // 16 × 0x10
    const paddingEnc = await crypto.subtle.encrypt(
      { name: 'AES-CBC', iv: lastBlock }, key, paddingPlain,
    );
    // WebCrypto adds its own padding block to our padding, take first 16 bytes
    const paddingBlock = new Uint8Array(paddingEnc).slice(0, 16);

    const ctWithPadding = new Uint8Array(ct.length + 16);
    ctWithPadding.set(ct);
    ctWithPadding.set(paddingBlock, ct.length);

    const plain = await crypto.subtle.decrypt(
      { name: 'AES-CBC', iv }, key, ctWithPadding,
    );
    return bufferToHex(plain);
  }
}

// ── AES-256-CBC encrypt with NULL padding (matches firmware/Python) ──
//    Output = hex(IV ‖ ciphertext)
//    Uses zero-byte padding to block boundary (NOT PKCS#7)
//    This matches Python: data_bytes + b'\x00' * pad_len
export async function aesEncrypt(dataHex, keyHex) {
  const keyBytes  = hexToBuffer(keyHex);
  const dataBytes = hexToBuffer(dataHex);

  // Null-pad to 16-byte block boundary (matches firmware)
  const padLen = dataBytes.length % 16 === 0 ? 0 : 16 - (dataBytes.length % 16);
  const padded = new Uint8Array(dataBytes.length + padLen); // auto-filled with 0x00
  padded.set(dataBytes);

  const iv = crypto.getRandomValues(new Uint8Array(16));

  const key = await crypto.subtle.importKey(
    'raw', keyBytes, { name: 'AES-CBC' }, false, ['encrypt'],
  );
  const ctRaw = await crypto.subtle.encrypt({ name: 'AES-CBC', iv }, key, padded);
  // WebCrypto adds its own PKCS#7 block on top — trim it back to our intended size
  const ct = new Uint8Array(ctRaw).slice(0, padded.length);

  const out = new Uint8Array(16 + ct.length);
  out.set(iv);
  out.set(ct, 16);
  return bufferToHex(out);
}

// ── JSON convenience wrappers ───────────────────────────────────
export async function aesEncryptJson(obj, keyHex) {
  // Use compact JSON (no spaces) to match Python: separators=(',',':')
  const jsonStr = JSON.stringify(obj);
  const jsonHex = stringToHex(jsonStr);
  return aesEncrypt(jsonHex, keyHex);
}

export async function aesDecryptJson(payloadHex, keyHex) {
  const plainHex = await aesDecrypt(payloadHex, keyHex);
  return JSON.parse(hexToString(plainHex));
}
